// Delivery Date Picker with next delivery date calculation
// Mainly used for physical items

import flatpickr from 'flatpickr';
import { defineElement, CSVToArray } from '~/utils';
import { ARROW_NEXT_SVG, ARROW_PREV_SVG } from '~/lib/constants';
import { isGeneralPreorderDate, isPrivatePreorderDate } from '~/utils/calendar-utils';
import { DateFormatters } from '~/utils/date-formatting';
import { DeliveryDateService } from '~/services/delivery-date-2';
import { GWUtils } from '~/utils/ca-utils';

class DeliveryDatePicker extends HTMLElement {
  /** @type {DeliveryDateService} */
  deliveryDateService;

  constructor() {
    super();

    this.ready = false

    this.flatpickrOptions = {
      minDate: 'today',
      altInput: true,
      altFormat: 'F j, Y',
      dateFormat: 'Z',
      monthSelectorType: 'static',
      wrap: true,
      nextArrow: ARROW_NEXT_SVG,
      prevArrow: ARROW_PREV_SVG,
      defaultDate: this.datePickerInput?.value,
      onDayCreate: this.#onDayCreate.bind(this),
      onOpen: this.#onDatePickerOpen.bind(this),
      disableMobile: true,
      appendTo: this,
      onReady: this.#onReady.bind(this),
      onMonthChange: this.#onMonthChange.bind(this),
      onChange: (selectedDates, dateStr, instance) => {
        this.dispatchEvent(new CustomEvent('date-picker::date-change', {
          detail: { date: dateStr },
          bubbles: true
        }));
      },
    };
  }

  get boardType() {
    if (this.orderEditMode) {
      const orderEdit = this.closest('ca-order-edit');
      const boardTypeInput = orderEdit?.querySelector('[name="board-type"]');
      if (boardTypeInput) return boardTypeInput.value;
    }
    const parentForm = this.closest('delivery-date-form');
    return parentForm?.boardType || 'cheese-board';
  }

  get isEgiftRedemption() {
    const parentForm = this.closest('delivery-date-form');
    return parentForm?.isEgiftRedemption || false;
  }

  get orderEditMode() {
    // Check direct attribute first (for customer accounts drawer), then parent form
    if (this.hasAttribute('order-edit-mode')) {
      return true;
    }
    const parentForm = this.closest('delivery-date-form');
    return parentForm?.orderEditMode || false;
  }

  get datePickerInputWrapper() {
    return this.querySelector('[data-date-picker-input-wrapper]');
  }

  get datePickerInput() {
    return this.datePickerInputWrapper?.querySelector('input');
  }

  get value() {
    return this.datePickerInput?.value;
  }

  connectedCallback() {
    const form = this.closest("delivery-date-form");
    this.deliveryDateService = form?.deliveryDateService;

    // If no parent form (e.g., in customer accounts order edit drawer),
    // create our own DeliveryDateService instance using window.Boarderie config
    if (!this.deliveryDateService && this.orderEditMode) {
      const productBlockOutDates = CSVToArray(this.getAttribute('product-block-out-dates'));
      const productSoldOutDates = CSVToArray(this.getAttribute('product-sold-out-dates'));
      let productPreorderDates = null;
      const preorderDatesAttr = this.getAttribute('product-preorder-dates');
      if (preorderDatesAttr) {
        try { productPreorderDates = JSON.parse(preorderDatesAttr); } catch (e) { /* ignore */ }
      }
      this.deliveryDateService = new DeliveryDateService(window.Boarderie, productBlockOutDates, productSoldOutDates, productPreorderDates);
    }

    if (!this.deliveryDateService) {
      console.error('DeliveryDateService not found');
      return;
    }

    this.#renderFlatpickr();

    this.dispatchEvent(new CustomEvent('date-picker::ready', {
      bubbles: true
    }))

    this.ready = true
  }

  /**
   * Open the date picker
   */
  open() {
    this.datePickerInputWrapper._flatpickr.open();
  }

  /**
   * Rerender the flatpickr calendar
   */
  rerenderFlatpickr() {
    this.#renderFlatpickr();
  }

  #renderFlatpickr() {
    this.flatpickr = flatpickr(this.datePickerInputWrapper, {
      ...this.flatpickrOptions,
    });

    if (this.orderEditMode) {
      const arrivalAttr = this.getAttribute('info-only-arrival-date');

      if (arrivalAttr) {
        this.flatpickr.setDate(GWUtils.toLocalDate(arrivalAttr));
      } else {
        const currentSelectedDate = new Date(this.flatpickr.latestSelectedDateObj);
        const isDessertBoard = this.boardType === 'dessert-board';
        const nextDay = new Date(currentSelectedDate.getTime());
        nextDay.setDate(nextDay.getDate() + 1);
        const isTuesdayDelivery = nextDay.getDay() === 2;
        const daysToAdd = isDessertBoard && !isTuesdayDelivery ? 2 : 1;
        const newSelectedDate = new Date(currentSelectedDate.setDate(currentSelectedDate.getDate() + daysToAdd));

        this.flatpickr.setDate(newSelectedDate);
      }
    }
  }

  #onDatePickerOpen() {
    // Focus the first available date
    const firstAvailableDate = this.flatpickr.calendarContainer.querySelector(
      'span[role="button"]:not([aria-disabled="true"])'
    );

    setTimeout(() => {
      if (firstAvailableDate) {
        firstAvailableDate.focus();
      }
    }, 100);

    const flatpickrNextMonth = this.flatpickr.calendarContainer.querySelector('.flatpickr-next-month');
    const flatpickrPrevMonth = this.flatpickr.calendarContainer.querySelector('.flatpickr-prev-month');

    if (flatpickrNextMonth) {
      flatpickrNextMonth.setAttribute('aria-label', 'Next month');
      flatpickrNextMonth.querySelector('svg').setAttribute('aria-hidden', 'true');
    }

    if (flatpickrPrevMonth) {
      flatpickrPrevMonth.setAttribute('aria-label', 'Previous month');
      flatpickrPrevMonth.querySelector('svg').setAttribute('aria-hidden', 'true');
    }

    // Product-level preorder: auto-navigate to preorder start month
    const productPreorderRange = this.deliveryDateService.productPreorderRange;
    if (productPreorderRange) {
      const preorderMonth = productPreorderRange.startDate.getMonth();
      const currentMonth = new Date().getMonth();
      if (preorderMonth !== currentMonth) {
        this.datePickerInputWrapper._flatpickr.jumpToDate(productPreorderRange.startDate, false);
      }
      return;
    }

    const nextAvailableDateMonth = this.deliveryDateService.deliveryDate.getMonth();
    const currentMonth = new Date().getMonth();
    if (nextAvailableDateMonth !== currentMonth) {
      this.datePickerInputWrapper._flatpickr.changeMonth(nextAvailableDateMonth, false);
    }

    // Check for preorder URL parameter and navigate to preorder month
    const urlParams = new URLSearchParams(window.location.search);
    const isPreorderSession = sessionStorage.getItem('preorder');

    if (urlParams.get('preorder') === 'true' || isPreorderSession) {
      const privatePreorder = window.Boarderie?.privatePreorder;
      const generalPreorder = window.Boarderie?.generalPreorder;

      let preorderFirstDate;
      if (isPreorderSession && privatePreorder?.enabled && privatePreorder?.preorderFirstDate) {
        preorderFirstDate = privatePreorder.preorderFirstDate;
      } else if (!isPreorderSession && generalPreorder?.enabled && generalPreorder?.preorderFirstDate) {
        preorderFirstDate = generalPreorder.preorderFirstDate;
      }

      if (preorderFirstDate) {
        const [month, day, year] = preorderFirstDate.split('-').map(Number);
        const preorderDate = new Date(year, month - 1, day);
        const preorderMonth = preorderDate.getMonth();
        const currentMonth = new Date().getMonth();

        if (preorderMonth !== currentMonth) {
          this.datePickerInputWrapper._flatpickr.jumpToDate(preorderDate, false);
        }
        return;
      }
    }

    const deliveryDateForm = this.closest('delivery-date-form');
    if (deliveryDateForm) {
      const defaultMonth = window.Boarderie?.calendarDefaultMonth;
      const defaultYear = window.Boarderie?.calendarDefaultYear;

      if (defaultMonth !== undefined && defaultMonth !== '') {
        const targetMonth = parseInt(defaultMonth, 10);
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        let targetYear;
        if (defaultYear && defaultYear !== '') {
          // Use the explicit year if provided
          targetYear = parseInt(defaultYear, 10);
        } else {
          // Auto-calculate: if target month is less than current month, use next year
          targetYear = targetMonth < currentMonth ? currentYear + 1 : currentYear;
        }

        if (targetMonth !== currentMonth || targetYear !== currentYear) {
          // Create a date in the target month/year and jump to it
          const targetDate = new Date(targetYear, targetMonth, 1);
          this.datePickerInputWrapper._flatpickr.jumpToDate(targetDate, false);
        }
        return;
      }

      const nextAvailableDate = this.deliveryDateService.deliveryDate;
      if (nextAvailableDate) {
        const nextAvailableDateMonth = nextAvailableDate.getMonth();

        const now = new Date();
        const currentMonth = now.getMonth();

        if (nextAvailableDateMonth !== currentMonth) {
          this.datePickerInputWrapper._flatpickr.jumpToDate(nextAvailableDate, false);
        }
      }
    }
  }

  #onReady(selectedDates, dateStr, fp) {
    this.#createJumpLink(fp);
    this.dispatchEvent(new CustomEvent('date-picker::calendar-ready', {
      bubbles: true
    }));
  }

  #onMonthChange(selectedDates, dateStr, fp) {
    this.#updateJumpLinkText(fp);
  }

  #createJumpLink(fp) {
    const template = this.getAttribute('calendar-jump-link-text');
    if (!template) return;

    this.jumpLinkTemplate = template;

    const monthsAttr = this.getAttribute('calendar-jump-link-months');
    this.jumpLinkMonths = monthsAttr
      ? monthsAttr.split(',').map((m) => parseInt(m.trim(), 10) - 1)
      : null;

    this.jumpLink = document.createElement('button');
    this.jumpLink.type = 'button';
    this.jumpLink.className = 'flatpickr-jump-link';
    this.jumpLink.addEventListener('click', () => {
      this.flatpickr.changeMonth(1);
    });

    fp.calendarContainer.appendChild(this.jumpLink);
    this.#updateJumpLinkText(fp);
  }

  #updateJumpLinkText(fp) {
    if (!this.jumpLink) return;

    const currentMonth = fp.currentMonth;

    if (this.jumpLinkMonths && !this.jumpLinkMonths.includes(currentMonth)) {
      this.jumpLink.style.display = 'none';
      return;
    }

    this.jumpLink.style.display = '';

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const nextMonth = (currentMonth + 1) % 12;
    const monthClass = 'flatpickr-jump-link--' + monthNames[currentMonth].toLowerCase();
    this.jumpLink.className = 'flatpickr-jump-link ' + monthClass;
    this.jumpLink.textContent = this.jumpLinkTemplate.replace('[[month]]', monthNames[nextMonth]);
  }

  #onDayCreate(dObj, dStr, fp, dayElem) {
    const { result: isBlocked, conditions: blockedConditions } = this.deliveryDateService.isBlockedDate(
      dayElem.dateObj
    );

    if (isBlocked) {
      this.#handleBlockedDay(dayElem, blockedConditions);
    } else {
      this.#handleUnblockedDay(dayElem);
    }
  }

  #handleBlockedDay(dayElem, blockedConditions) {
    dayElem.classList.add('flatpickr-disabled');
    dayElem.setAttribute('aria-disabled', 'true');
    dayElem.setAttribute('aria-hidden', 'true');
    dayElem.setAttribute('tabindex', '0');
    dayElem.setAttribute('role', 'button');
    dayElem.setAttribute('aria-label', `Unavailable ${this.#formatDateToFull(dayElem.dateObj)}`);

    if (blockedConditions.includes('product-soldout')) {
      this.#addDayContent(dayElem, 'SOLD OUT', 'flatpickr-day--soldout', true);
    }
  }

  #handleUnblockedDay(dayElem) {
    dayElem.setAttribute('tabindex', '0');
    dayElem.setAttribute('role', 'button');
    dayElem.setAttribute('aria-label', `Select ${this.#formatDateToFull(dayElem.dateObj)}`);

    const matchingFees = this.deliveryDateService.getFees(dayElem.dateObj, this.boardType);

    // In order edit mode, disable days that have fees (surcharge, premium, date-based)
    if (this.orderEditMode && matchingFees.length) {
      dayElem.classList.add('flatpickr-disabled');
      dayElem.setAttribute('aria-disabled', 'true');
      dayElem.setAttribute('aria-hidden', 'true');
      dayElem.setAttribute('aria-label', `Unavailable ${this.#formatDateToFull(dayElem.dateObj)}`);
      return;
    }

    // Fee day handling
    if (matchingFees.length) {
      const totalFeeDollars = matchingFees.reduce((acc, fee) => acc + (fee.cost || 0), 0);
      const totalFeeCents = totalFeeDollars * 100;
      const content = '+' + Shopify.formatMoney(totalFeeCents, '${{ amount_no_decimals }}');
      const className = 'flatpickr-day--fee';
      const color = matchingFees[0].color;

      this.#addDayContent(dayElem, content, className, false, color);
      return;
    }

    // Preorder handling
    const isPrivatePreorder = isPrivatePreorderDate(dayElem.dateObj);
    const isGeneralPreorder = isGeneralPreorderDate(dayElem.dateObj);

    if ((isPrivatePreorder || isGeneralPreorder) && !this.isEgiftRedemption) {
      let discount, discountType, discountLabel, discountColor;

      // Determine discount details
      if (isPrivatePreorder) {
        ({ discount, discountType, discountLabel, discountColor } = this.deliveryDateService.privatePreorder);
      } else if (isGeneralPreorder) {
        ({ discount, discountType, discountLabel, discountColor } = this.deliveryDateService.generalPreorder);
      }

      // Format discount content
      let content = `${discount}%`;

      if (discountType === 'dollar') {
        content = `$${discount}`;
      }

      if (discountLabel) {
        content += ` ${discountLabel}`;
      }

      this.#addDayContent(dayElem, content, 'flatpickr-day--preorder', false, discountColor);

      return;
    }

    // Free day handling
    const freeDayColor = this.deliveryDateService.freeDayColor;
    this.#addDayContent(dayElem, 'FREE', 'flatpickr-day--free', false, freeDayColor);
  }

  #addDayContent(dayElem, content, className, removeDate = false, bgColor) {
    if (removeDate) {
      dayElem.textContent = '';
    }

    const span = document.createElement('span');
    span.textContent = content;
    dayElem.appendChild(span);
    dayElem.classList.add(className);

    if (bgColor) {
      dayElem.style.backgroundColor = bgColor;
    }
  }

  #formatDateToFull(dateObj) {
    return DateFormatters.toFullString(dateObj);
  }
}

if (!customElements.get('delivery-date-picker')) {
  defineElement('delivery-date-picker', DeliveryDatePicker);
}